import { catchError, map, Observable, of, tap } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { EventDTO } from '../../../../dto/event/event.dto';
import { environment } from '../../../../environment/environment';
import { AuthService } from '../../../../services/auth/auth.service';

export function events(componentDataSourceService: ComponentDataSourceService) {
  // Get AuthService
  const authService = componentDataSourceService.injector.get(AuthService);
  
  return {
    name: 'Events',
    description: 'Lists all events',
    execute: (params?: LoadDataParams) => {
      
      // CRITICAL: Block requests without date filters to prevent loading all events
      const hasDateFilters = params?.queryParams?.['startDate'] || params?.queryParams?.['endDate'];
      if (!hasDateFilters) {
        console.error('[Events DataSource] BLOCKED: No date filters provided - this would load all events!');
        console.error('[Events DataSource] Params received:', JSON.stringify(params, null, 2));
        console.error('[Events DataSource] Call stack:', new Error().stack);
        console.error('[Events DataSource] Returning empty array to prevent loading all events');
        return of([]); // Return empty array instead of making the API call
      }
      
      
      // Build query parameters
      const queryParams: any = {};
      
      // Handle standard search query
      if (params?.searchQuery) {
        queryParams.search = params.searchQuery;
      }
      
      // Handle filters from LoadDataParams
      if (params?.filters?.length) {
        queryParams.filters = JSON.stringify(params.filters);
      }
      
      // Handle queryParams passed directly (used by DunaEventsCalendarComponent)
      if (params?.queryParams) {
        
        // Handle search from queryParams
        if (params.queryParams['search']) {
          queryParams.search = params.queryParams['search'];
        }
        
        // Handle event types
        if (params.queryParams['eventTypes']) {
          queryParams.event_types = Array.isArray(params.queryParams['eventTypes']) 
            ? params.queryParams['eventTypes'].join(',') 
            : params.queryParams['eventTypes'];
        }
        
        // Handle organization IDs (check both camelCase and snake_case)
        if (params.queryParams['organizationIds'] || params.queryParams['organization_ids']) {
          const orgIds = params.queryParams['organizationIds'] || params.queryParams['organization_ids'];
          queryParams.organization_ids = Array.isArray(orgIds) 
            ? orgIds.join(',') 
            : orgIds;
        }
        
        // Handle date ranges
        if (params.queryParams['startDate']) {
          queryParams.startDate = params.queryParams['startDate'];
        }
        if (params.queryParams['endDate']) {
          queryParams.endDate = params.queryParams['endDate'];
        }
        
        // Handle visibility
        if (params.queryParams['visibility']) {
          queryParams.visibility = Array.isArray(params.queryParams['visibility']) 
            ? params.queryParams['visibility'].join(',') 
            : params.queryParams['visibility'];
        }
        
        // Handle educational path IDs
        if (params.queryParams['educational_path_ids']) {
          queryParams.educational_path_ids = params.queryParams['educational_path_ids'];
        }
        if (params.queryParams['educational_path_version_ids']) {
          queryParams.educational_path_version_ids = params.queryParams['educational_path_version_ids'];
        }
        if (params.queryParams['module_ids']) {
          queryParams.module_ids = params.queryParams['module_ids'];
        }
        if (params.queryParams['location_guids']) {
          queryParams.location_guids = params.queryParams['location_guids'];
        }
        if (params.queryParams['hide_full']) {
          queryParams.hide_full = params.queryParams['hide_full'];
        }
        if (params.queryParams['exclude_participated']) {
          queryParams.exclude_participated = params.queryParams['exclude_participated'];
        }
        if (params.queryParams['lat']) {
          queryParams.lat = params.queryParams['lat'];
        }
        if (params.queryParams['lng']) {
          queryParams.lng = params.queryParams['lng'];
        }
        if (params.queryParams['radius_km']) {
          queryParams.radius_km = params.queryParams['radius_km'];
        }
      }

      // Group-calendar events are shown only in duna-events-group-calendar
      queryParams.exclude_group_events = 'true';

      return componentDataSourceService.http.get<EventDTO[]>(`${environment.apiUrl}/events`, {
        params: queryParams
      }).pipe(
        tap(response => {
        }),
        catchError(error => {          
          // For 404 errors with "No events found", this is expected and not a real error
          if (error.status === 404) {
          } else {
          }
          
          return of([]); // Return empty array instead of throwing to prevent UI breaks
        }),
        map(result => {
          if (!result) {
            console.error('[Events DataSource] No result received from API');
            return [];
          }

          try {
            const resultArray = (Array.isArray(result) ? result : Object.values(result)) as EventDTO[];
            
            // Filter out 'exercise' events if user is not authenticated
            let filteredResults = resultArray;
            if (!authService.isAuthenticated) {
              filteredResults = resultArray.filter(event => event.event_type !== 'exercise');
            }
            
            // Log information about active vs inactive events
            const activeEvents = filteredResults.filter(event => event.is_active);
            const inactiveEvents = filteredResults.filter(event => !event.is_active);
            
            filteredResults = filteredResults.filter(
              (event) => event.user_group_id == null || Number(event.user_group_id) <= 0
            );

            return filteredResults.map((event: EventDTO) => {
              // Ensure is_active is properly handled
              const isActive = event.is_active !== undefined ? event.is_active : true;
              
              return {
                ...event,
                date: event.start_date || '',
                // Ensure the is_active property is preserved
                is_active: isActive
              };
            });
          } catch (error) {
            console.error('[Events DataSource] Error transforming data:', error);
            return []; // Return empty array instead of throwing to prevent UI breaks
          }
        })
      );
    },
    settings: {
      columns: [
        { key: 'event_id', header: 'ID', type: 'number' },
        { key: 'event_type', header: 'Type', type: 'text' },
        { key: 'start_date', header: 'Start Date', type: 'date' },
        { key: 'end_date', header: 'End Date', type: 'date' },
        { key: 'description', header: 'Description', type: 'text' },
        { key: 'visibility', header: 'Visibility', type: 'text' }
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      defaultSortColumn: 'date',
      defaultSortDirection: 'asc'
    }
  };
} 